import React from 'react';
import { Chessboard } from 'react-chessboard';

// Chess board wrapper with turn indicator, timer, admin controls, tooltip, and game-over banner
const ChessBoardPanel = ({
  game,
  chess,
  chessPosition,
  boardOrientation,
  isAdmin,
  playerTeam,
  remainingTime,
  optionSquares,
  previewedMove,
  hoveredSquare,
  hoverPos,
  getPieceName,
  onDrop,
  onSquareClick,
  boardWrapperRef,
  onBoardMouseMove,
  onBoardMouseLeave,
  revertLastMove,
  soundMuted,
  onToggleSound,
}) => {
  const activePoll = !isAdmin && playerTeam
    ? (playerTeam === 'white' ? game.activePolls?.white : game.activePolls?.black)
    : null;

  // Build square styles for move preview
  const previewSquares = previewedMove ? {
    [previewedMove.from]: {
      background: 'rgba(107, 143, 255, 0.5)'
    },
    [previewedMove.to]: {
      background: 'radial-gradient(circle, rgba(107, 143, 255, 0.8) 25%, transparent 25%)',
      borderRadius: '50%'
    }
  } : {};

  const boardOptions = {
    position: chessPosition,
    onPieceDrop: onDrop,
    onSquareClick: onSquareClick,
    boardOrientation: boardOrientation,
    customBoardStyle: {
      borderRadius: '8px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
    },
    customDarkSquareStyle: { backgroundColor: '#4a5568' },
    customLightSquareStyle: { backgroundColor: '#e2e8f0' },
    squareStyles: { ...optionSquares, ...previewSquares },
    arePiecesDraggable: !isAdmin && game.status === 'in_progress' && !activePoll,
    id: `board-${boardOrientation}`
  };

  return (
    <div className="chess-board-container">
      <div className="turn-indicator">
        <div className="turn-info">
          Current Turn: <span className={`turn-${game.currentTurn}`}>{game.currentTurn === 'white' ? 'White' : 'Black'}</span>
          {chess.isCheck() && <span className="check-indicator"> - CHECK!</span>}
        </div>
        <button
          type="button"
          className="sound-toggle-button"
          onClick={onToggleSound}
          aria-label={soundMuted ? 'Unmute sound effects' : 'Mute sound effects'}
          title={soundMuted ? 'Unmute sound effects' : 'Mute sound effects'}
        >
          {soundMuted ? '🔇' : '🔊'}
        </button>
        {!isAdmin && playerTeam && remainingTime !== null && game.status === 'in_progress' && game.currentTurn === playerTeam && (
          <div className={`timer-display ${remainingTime <= 10 ? 'timer-critical' : ''}`}>
            {(() => {
              const timerData = playerTeam === 'white' ? game.turnTimer?.white : game.turnTimer?.black;
              const phase = timerData?.phase === 'voting' ? 'Voting' : 'Suggesting';
              const minutes = Math.floor(remainingTime / 60);
              const seconds = remainingTime % 60;
              return (
                <>
                  <span className="timer-phase">{phase}:</span>
                  <span className="timer-time">{minutes}:{seconds.toString().padStart(2, '0')}</span>
                </>
              );
            })()}
          </div>
        )}
        {isAdmin && (
          <div className="admin-controls">
            <button
              onClick={revertLastMove}
              className="revert-move-button"
              disabled={!game.moveHistory || game.moveHistory.length === 0}
            >
              Revert Last Move
            </button>
          </div>
        )}
      </div>
      <div
        className="chessboard-wrapper"
        ref={boardWrapperRef}
        onMouseMove={onBoardMouseMove}
        onMouseLeave={onBoardMouseLeave}
      >
        {game && boardOrientation && (
          <Chessboard options={boardOptions} />
        )}
        {/* Piece name + assigned player tooltip — follows the cursor */}
        {(() => {
          const info = hoveredSquare ? getPieceName(hoveredSquare) : null;
          if (!info) return null;
          // Sit just above the cursor; flip below when hovering near the top edge.
          const pos = hoverPos || { x: 0, y: 0 };
          const flipBelow = pos.y < 64;
          const tooltipStyle = {
            left: `${pos.x}px`,
            top: `${pos.y}px`,
            transform: flipBelow
              ? 'translate(-50%, 24px)'
              : 'translate(-50%, calc(-100% - 16px))',
          };
          return (
            <div className="piece-tooltip" style={tooltipStyle}>
              <span className="piece-tooltip-name">{info.pieceLabel}</span>
              {info.playerLabel ? (
                <span className="piece-tooltip-player">
                  {info.playerLabel}
                  {info.isAI && <span className="piece-tooltip-ai">AI</span>}
                </span>
              ) : (
                <span className="piece-tooltip-player piece-tooltip-unassigned">Unassigned</span>
              )}
            </div>
          );
        })()}
      </div>
      {game.status === 'completed' && (
        <div className="game-over-banner">
          <h3>Game Over!</h3>
          <p>{game.endReason}</p>
          {game.winner && <p className="winner-text">Winner: {game.winner === 'white' ? 'White Team' : 'Black Team'}</p>}
        </div>
      )}
    </div>
  );
};

export default ChessBoardPanel;
